import React from 'react';
import { Progress } from '@/components/ui/progress';
import { Wrench } from 'lucide-react';
import { MaintenanceNotification } from '@/contexts/NotificationContext';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

interface MaintenanceNotificationToastProps {
  notification: MaintenanceNotification;
}

const MaintenanceNotificationToast: React.FC<MaintenanceNotificationToastProps> = ({ notification }) => {
  // Calcular el tiempo restante estimado
  const calculateTimeRemaining = () => {
    const createdAt = parseISO(notification.createdAt);
    const estimatedEndTime = new Date(createdAt);
    estimatedEndTime.setHours(estimatedEndTime.getHours() + notification.estimatedTimeHours);
    
    const now = new Date();
    const totalDuration = estimatedEndTime.getTime() - createdAt.getTime();
    const elapsed = now.getTime() - createdAt.getTime();
    
    // Calcular el porcentaje de tiempo transcurrido (invertido para mostrar tiempo restante)
    const percentElapsed = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
    const percentRemaining = Math.max(0, 100 - percentElapsed);
    
    return percentRemaining;
  };

  // Determinar el color de la barra de progreso según el tiempo restante
  const getProgressColor = (timeRemaining: number) => {
    if (timeRemaining > 66) return 'bg-green-500';
    if (timeRemaining > 33) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const timeRemaining = calculateTimeRemaining();
  const progressColor = getProgressColor(timeRemaining);

  return (
    <div className="flex flex-col w-full max-w-sm bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="flex items-center p-4 bg-amber-50 border-b border-amber-100">
        <div className="p-2 rounded-full bg-amber-100 text-amber-600 mr-3">
          <Wrench className="h-6 w-6" />
        </div>
        <div>
          <h4 className="font-semibold text-base">¡Pantalla en mantenimiento!</h4>
          <p className="text-sm text-amber-700">{notification.screenName}</p>
        </div>
      </div>
      
      <div className="p-4">
        <div className="mb-3">
          <div className="text-sm mb-1 flex justify-between">
            <span>Tiempo restante estimado:</span>
            <span>{notification.estimatedTimeHours} horas</span>
          </div>
          <Progress value={timeRemaining} className={progressColor} />
        </div>
        
        {notification.screenLocation && (
          <p className="text-sm text-gray-600 mb-1">
            Ubicación: {notification.screenLocation}
          </p>
        )}
        
        <p className="text-xs text-gray-500">
          Reportado {formatDistanceToNow(parseISO(notification.createdAt), { addSuffix: true, locale: es })}
        </p>
      </div>
    </div>
  );
};

export default MaintenanceNotificationToast; 